/**
 * Data Import API Client
 *
 * API functions for data import and initial setup.
 */

import { apiRequest } from './api';

/**
 * Import result
 */
export interface ImportResult {
  success: boolean;
  created: number;
  updated: number;
  errors: string[];
  message?: string;
}

/**
 * Data summary
 */
export interface DataSummary {
  niveaus: number;
  employees: number;
  tasks: number;
  competencies: {
    total: number;
    basis: number;
    taak: number;
  };
  taskAssignments: number;
}

/**
 * Seed all results
 */
export interface SeedAllResults {
  message: string;
  results: {
    niveaus: ImportResult;
    tasks: ImportResult;
    competencies: ImportResult;
  };
}

/**
 * Task import data
 */
export interface TaskImportData {
  name: string;
  beschrijving?: string;
  weging: number;
  niveauId: number;
  isActief?: boolean;
}

/**
 * Competency import data
 */
export interface CompetencyImportData {
  name: string;
  beschrijving?: string;
  type: 'basis' | 'taak';
  taskName?: string;
}

/**
 * Data Import API functions
 */
export const dataImportApi = {
  /**
   * Get data summary
   */
  async getSummary(): Promise<DataSummary> {
    return apiRequest<DataSummary>('/import/summary');
  },

  /**
   * Seed niveaus with default data
   */
  async seedNiveaus(): Promise<ImportResult> {
    return apiRequest<ImportResult>('/import/seed-niveaus', {
      method: 'POST',
    });
  },

  /**
   * Seed all default data (niveaus, tasks, competencies)
   */
  async seedAll(): Promise<SeedAllResults> {
    return apiRequest<SeedAllResults>('/import/seed-all', {
      method: 'POST',
    });
  },

  /**
   * Import tasks from JSON
   */
  async importTasks(tasks: TaskImportData[]): Promise<ImportResult> {
    return apiRequest<ImportResult>('/import/tasks', {
      method: 'POST',
      body: JSON.stringify({ tasks }),
    });
  },

  /**
   * Import competencies from JSON
   */
  async importCompetencies(competencies: CompetencyImportData[]): Promise<ImportResult> {
    return apiRequest<ImportResult>('/import/competencies', {
      method: 'POST',
      body: JSON.stringify({ competencies }),
    });
  },

  /**
   * Sync employees from MySQL
   */
  async syncEmployees(): Promise<ImportResult> {
    return apiRequest<ImportResult>('/import/sync-employees', {
      method: 'POST',
    });
  },
};
